/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
// eslint-disable-next-line no-restricted-imports
import isUndefined from 'lodash.isundefined';
import Router from 'next/router';
import qs from 'qs';
import {translate} from '@ion/core/src/locale/locale';
import {info} from '@ion/core/src/logger/logger';
import {
  isObjectEmpty
} from '@ion/core/src/utils/ObjectUtil';
import {
  titleCase
} from '@ion/core/src/utils/StringUtil';

// defines all predefined route definitions
export enum Route {
  LOGIN = 'login',
  DASHBOARD = 'dashboard'
};

const FeaturesMap = {};

/**
 * Provides the initial point of entry for configuring BMW Portal Route management.
 *
 * Invoked when the root application component (_app.js) is first mounted, after which
 * all query changes are referenced for subsequent inclusion within each route when the
 * current page changes.
 */
export const initializeRouter = () => {
  info('Initializing Application Router ...');
  setQuery();
  Router.events.on('routeChangeStart', function() {
    setQuery();
  });
};

/**
 * Provides the single point of entry for changing a page within BMW Portal. This function
 * must be used explicitly and Next Router may not be used directly.
 *
 * The changePage function allows for ensuring all pages meet the requirements of BMW Portal,
 * including, but not limited to, verifying Route definitions, inclusion of existing query
 * parameters, etc..
 *
 * ```typescript
 *
 *   import changePage {Route} from '@/routes/RouteDefinitions';
 *
 *   // navigate to the dashboard page, including existing query parameters by
 *   // default ...
 *   changePage(Route.DASHBOARD);
 *
 *   // navigate to a specific dashboard page (e.g. /dashboard/123), including existing
 *   // query parameters, by default ...
 *   changePage(Route.DASHBOARD, `/${data.id}`);
 *
 *   // navigate to the dashboard page, excluding existing query parameters ...
 *   changePage(Route.DASHBOARD, null, false);
 *
 *   // navigate to a specific dashboard page (e.g. /dashboard/123), excluding existing
 *   // query parameters ....
 *   changePage(Route.DASHBOARD, `/${data.id}`, false);
 *
 * ```
 *
 * @param {Route} route The Route to which the application is to navigate.
 * @param {string} path An optional path to include within the route.
 * @param {boolean} includeParams When true (default), the previous pages query parameters
 */
export const changePage = (route: Route, path = '', includeParams = true): void => {
  const uri = getRoute(route, path);
  const url = includeParams && !isObjectEmpty(_query)? `${uri}?${qs.stringify(_query)}` : uri;
  Router.push(url);
};

/**
 * The getRoute function provides the single point of access which should be used
 * for retrieving a given route. The helper ensures that the route being referenced
 * has the correct base path prefix and maps to an existing, predefined route.
 *
 * @param {Route} route The Route to which the application is to navigate.
 * @param {string} path An optional path to include within the route.
 */
export const getRoute = (route: Route, path?: string): string => {
  return `/${route?.replace(/^\/+/, '')}${path || ''}`.split('?')?.[0];
};

/**
 * @private
 */
let _query;

/**
 * Provides a reference to the current query parameters passed to the current page,
 * or forwarded to the current page from subsequent pages.
 */
export const getQuery = () => {
  return _query;
};

/**
 * Allows for setting the query parameters to be passed to each BMW Portal page when
 * the page is loaded. This function should generally not be used directly as it is
 * primarily used internally for updating the query parameters passed to BMW Portal
 * pages.
 */
export const setQuery = (querystring?: string): void => {
  _query = qs.parse(querystring || location.search, {ignoreQueryPrefix: true});
};

/**
 * Provides a utility for determining if the application is being run in debug mode
 * via the `?debug? Querystring parameter.
 *
 * This function is primarily intended for determining if the application is to be
 * run in debug mode prior to initializing the router in order to enable / disable
 * logging in higher environments.
 */
export const isDebug = (): boolean => {
  const query = getQuery() || qs.parse(location.search, {ignoreQueryPrefix: true});
  return !isUndefined(query?.debug);
};

/**
 * Provides a utility which determines if the provided route is the current route.
 */
export const isRoute = (route: Route): boolean => {
  return window.location.pathname.indexOf(route) > -1;
};

/**
 * The getRouteDefinition function provides a utility which returns the Route for
 * the given path
 *
 * ```typescript
 *    import {getRouteDefinition} from '@/routes/RouteDefinitions';
 *
 *    getRouteDefinition('/tracking'); // Route.TRACKING
 *    getRouteDefinition('/admin/assets'); // Route.ADMIN_ASSETS
 *
 * ```
 */
export const getRouteDefinition = (path: string): Route => {
  const route = path?.replace(/^\/|\/$/g, '').split('?')?.[0];
  const name  = Object.keys(Route).find((name: string) => {
    return Route[name] === route;
  });
  return Route[name];
};

/**
 * The getRouteByFeature function provides a utility which returns the iOn Route for
 * the given iOn Feature.
 *
 * ```typescript
 *    import {getRouteDefinition} from '@/routes/RouteDefinitions';
 *
 *    getRouteByFeature(TRACKING); // Route.TRACKING
 *    getRouteByFeature(ADMIN_ASSETS); // Route.ADMIN_ASSETS
 *
 * ```
 */
export const getRouteByFeature = (id: string, fallback = true): Route => {
  let route = FeaturesMap[id];
  if (!route && fallback) {
    route = Route.DASHBOARD;
  }
  return route;
};

/**
 * The getRouteLabel function provides a utility which returns the route Label for
 * the given Route
 *
 * ```typescript
 *    import {getRouteLabel} from '@/routes/RouteDefinitions';
 *
 *    getRouteDefinition(Route.DASHBOARD); // dashboard
 *
 * ```
 */
export const getRouteLabel = (route: string, useTitleCase = false): string => {
  if (route.match('/')) {
    const [rootRoute, subRoute] = route.split('/');
    const capital = subRoute.charAt(0);
    const page    = subRoute.replace(capital, capital.toUpperCase());
    const entry   = translate(`nav:side.${rootRoute}${page}`);
    return useTitleCase ? titleCase(entry) : entry;
  }
  return useTitleCase ? titleCase(route) : route;
};

/**
 * The getPageTitle function provides a simple utility which returns the page title
 * for the given route.
 *
 * ```typescript
 *   import {getPageTitle} from '@/routes/RouteDefinitions';
 *
 *   getPageTitle(Route.ADMIN_ASSETS); // Assets
 *   getPageTitle(Route.TRACKING); // Tracking
 *
 * ```
 */
export const getPageTitle = (route: Route | string) => {
  let [, prefix, postfix] = route.substr(0, route.length).split('/');
  // if postfix contains a string integer just display the prefix
  if (parseInt(postfix)) {
    return translate(`nav:side.${prefix}`);
  }
  if (postfix) {
    postfix = `${postfix.charAt(0).toUpperCase()}${postfix.slice(1)}`;
    return translate(`nav:side.${prefix}${postfix}`);
  }
  return translate(`nav:side.${prefix}`);
};

export default changePage;
